import React, { useEffect, useRef, useState } from 'react';
import { softphoneApi, wallboardApi, SOFTPHONE_SESSION_ID } from '../../api/client';
import { useScreenRecording } from '../../hooks/useScreenRecording';

/**
 * AgentStatusBar — Ready / AUX / Wrap-up / Logout.
 *
 * Behavior:
 *  - Agent starts as "Not Ready" on mount. No automatic Ready.
 *  - Clicking Ready sends QueueAdd + Pause=false via /api/softphone/state.
 *  - AUX opens a reason picker (Lunch / Break / Meeting / Training / etc.).
 *  - Wrap-up = paused with reason="wrap-up".
 *  - Logout = QueueRemove and disconnects SIP (parent decides).
 *
 * Layout: compact horizontal flex that wraps cleanly inside the softphone
 * box so every button is reachable on narrow widths.
 */

const AUX_REASONS = [
  { code: 'lunch',                 label: 'Lunch'                 },
  { code: 'break',                 label: 'Break'                 },
  { code: 'meeting',               label: 'Meeting'               },
  { code: 'training',              label: 'Training'              },
  { code: 'admin',                 label: 'Admin'                 },
  // 2026-08-04: Fernando requested a dedicated "After Call Work" (ACW)
  // reason for post-call wrap activity the agent handles off-queue.
  { code: 'after_call_work',       label: 'After Call Work'       },
  // 2026-07-15: Helen (QA supervisor) requested a dedicated catch-all
  // for time an agent spends off-queue on ad-hoc / non-planned work
  // (project spikes, escalations, one-off requests, etc). Sits above
  // "Other" so reviewers see the more descriptive option first.
  { code: 'unscheduled_activity',  label: 'Un-scheduled activity' },
  { code: 'other',                 label: 'Other'                 },
];

function StateBadge({ state, reason, callDirection }) {
  const palette = {
    ready:     { bg: '#3BD162', text: '#fff', label: 'Ready'      },
    aux:       { bg: '#F5A623', text: '#fff', label: 'AUX'        },
    wrap:      { bg: '#2196F3', text: '#fff', label: 'Wrap-up'    },
    logout:    { bg: '#9CA3AF', text: '#fff', label: 'Not Ready'  },
    notready:  { bg: '#9CA3AF', text: '#fff', label: 'Not Ready'  },
    // 2026-08-17: reflect the LIVE call state on the softphone so agents see
    // "On Call" (matching the ACD > Queues real-time dashboard) instead of
    // Ready / Wrap-up while they are actually on a call/conference.
    on_call:   { bg: '#7C3AED', text: '#fff', label: 'On Call'    },
  };
  const p = palette[state] || palette.notready;
  const dirLabel = state === 'on_call' && callDirection
    ? (callDirection === 'outbound' ? '(outbound)' : '(inbound)')
    : null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 999,
      background: p.bg, color: p.text,
      fontSize: 11, fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: p.text, opacity: 0.7 }} />
      {p.label}
      {dirLabel && <span style={{ opacity: 0.85, fontWeight: 400 }}>{dirLabel}</span>}
      {reason && state === 'aux' && <span style={{ opacity: 0.85, fontWeight: 400 }}>· {reason}</span>}
    </span>
  );
}

export default function AgentStatusBar({ extension, onLoggedOut, callActive = false, callState = null, callDirection = null, multiLineActive = false }) {
  // Agent-state hardening (2026-07-09): state buttons are disabled while a
  // call is active. Server also enforces this (409 cannot_change_state_while_on_call)
  // but we should never let the user click a button that we know will fail.
  // Single-active-session (2026-07-29): if a newer tab/window takes over this
  // agent's softphone, this tab goes PASSIVE — state controls locked + a
  // banner. Prevents the multi-tab storm where background tabs fought over
  // ACD state (agent "stuck in Ready").
  const [superseded, setSuperseded] = useState(false);
  const stateLocked = !!callActive || superseded;
  // Phase 2 screen recording (2026-06-22). Lifecycle: start on first Ready,
  // stop on Logout. Recording continues across AUX / Wrap so audio + screen
  // stay correlated for the whole shift. The browser-side hook gates itself
  // on the backend /should-record endpoint (agent toggle), so this is safe
  // to call unconditionally.
  const screenRec = useScreenRecording();
  const screenRecStartedRef = useRef(false);
  // Start in NOT READY state per Fernando's request — agent must click Ready
  // explicitly to start taking calls. The mount effect below ALSO syncs this
  // to the backend so the agent is genuinely removed from all queues until
  // they explicitly click Ready (prevents "I just logged in but got a call").
  const [state, setState]     = useState('notready');
  const [reason, setReason]   = useState(null);
  const [busy, setBusy]       = useState(false);
  const [showAux, setShowAux] = useState(false);
  const [error, setError]     = useState(null);
  // 2026-08-10: AUX reasons are now PER-TENANT + data-driven (Admin > AUX
  // Codes). Fetch the effective set for this agent's tenant on mount; fall
  // back to the built-in AUX_REASONS list if the fetch fails (fail-open so a
  // transient error never leaves the agent with no reasons to pick).
  const [auxReasons, setAuxReasons] = useState(AUX_REASONS);
  const [wrapRemain, setWrapRemain] = useState(0);   // seconds remaining
  const supersededRef = useRef(false);
  const wrapTimerRef = useRef(null);
  // 2026-08-17: debounce timer for the wrap-on-end-of-interaction settle.
  const wrapSettleTimerRef = useRef(null);
  const mountedRef = useRef(false);
  // Ref mirror of `state` so event listeners always read the LATEST value,
  // not whatever was captured by the useEffect closure. Fixes a race where
  // `cx-call-ended-inbound` arrived during a stale render and the guard
  // saw the previous state. 2026-06-19.
  const stateRef = useRef('notready');
  // 2026-08-17: ref mirror of the LIVE callState so the wrap-trigger handlers
  // can tell whether the agent is STILL on a call/conference.
  const callStateRef = useRef(null);
  // 2026-08-06 FIX: snapshot the agent's ACD state at the moment a call
  // becomes active, so the post-call wrap countdown can RESTORE it instead of
  // always flipping to Ready. Root cause of the recurring "agent set AUX/Lunch
  // but kept getting calls and reverting to Ready" bug (61025 Jeanie Cruz +
  // random reports): startWrapCountdown hard-coded change('ready') at the end,
  // so ANY call that touched a not-ready/AUX agent dragged them back to Ready
  // after wrap (esp. no-queue agents who still get direct-to-extension calls,
  // which AUX never gates). We now restore the pre-call state (aux+reason /
  // notready) verbatim. Holds {state, reason} or null when the pre-call state
  // was a routable one (ready) where Ready-after-wrap is correct.
  const preCallStateRef = useRef(null);

  const clearWrapTimer = () => {
    if (wrapTimerRef.current) {
      clearInterval(wrapTimerRef.current);
      wrapTimerRef.current = null;
    }
    // 2026-08-17: also cancel any pending wrap-settle debounce so an explicit
    // state change (Ready/AUX/Logout) or a new call stops a queued wrap.
    if (wrapSettleTimerRef.current) {
      clearTimeout(wrapSettleTimerRef.current);
      wrapSettleTimerRef.current = null;
    }
    setWrapRemain(0);
  };

  // `silent`: when true, do NOT toggle the `busy` flag. Used for AUTOMATED
  // state transitions (the wrap-up countdown's change('wrap') and the final
  // change('ready')). Toggling `busy` on those made all four state buttons
  // flash between dim/bright right after End All / line-X, looking like they
  // were being clicked. User-initiated clicks still pass silent=false so the
  // buttons correctly show a pressed/disabled state during the round trip.
  const change = async (next, nextReason = null, { silent = false } = {}) => {
    if (!silent) setBusy(true);
    setError(null);
    try {
      // 2026-09-02 FIX (Bug 2 — "agents get calls while on break/lunch"):
      // If the agent DELIBERATELY picks AUX / break / lunch WHILE A CALL IS
      // ACTIVE, that is a mid-call intent the wrap-up countdown must honor at
      // call-end. The backend's `set_state` correctly stashes prior_state=aux
      // (deferred-intent path), but the CLIENT wrap countdown restores
      // `preCallStateRef` — snapshotted at CALL START (e.g. `ready`) — and
      // would drag the agent back to Ready, so the queue delivers the next
      // call to someone who thought they were on lunch (proven live for 61059,
      // 2026-09-02). Capture the mid-call AUX choice into preCallStateRef so
      // the existing wrap-restore logic lands the agent in AUX, not Ready.
      // (silent restores from the wrap countdown itself pass silent=true and
      // must NOT re-capture — they ARE the restore.)
      if (next === 'aux' && callActive && !silent) {
        preCallStateRef.current = { state: 'aux', reason: nextReason || 'AUX' };
        console.log('[CX-WRAP] mid-call AUX captured as post-call intent:',
                    nextReason || 'AUX');
      }
      // 'notready' is a UI-only state; map to backend 'logout' (removes from queue).
      const backendState = next === 'notready' ? 'logout' : next;
      const resp = await softphoneApi.setState(backendState, nextReason);
      // Map back: if user asked for 'notready' we keep that UI label.
      setState(next === 'notready' ? 'notready' : resp.state);
      setReason(resp.reason || null);
      if (next !== 'wrap') clearWrapTimer();

      // ---- Screen recording lifecycle (Option A, 2026-07-02 v2) -----------
      // Fernando confirmed Option A: prompt ONCE per shift on first Ready
      // click. After that, MediaRecorder starts/stops silently per call.
      // On Logout / Not-Ready, releaseStream() drops the MediaStream so the
      // browser-native screen-share indicator disappears until next Ready.
      //
      // acquireStream() short-circuits silently if the agent has
      // screen_recording_enabled=false in the DB (backend `should-record`
      // check), so agents whose toggle is off never see a prompt.
      if (next === 'ready' && !screenRecStartedRef.current) {
        screenRecStartedRef.current = true;
        screenRec.acquireStream({ agentExtension: extension || null })
          .catch((err) => console.warn('[CX-SR] acquireStream failed', err));
      }
      if (next === 'logout' || next === 'notready') {
        if (screenRecStartedRef.current) {
          screenRecStartedRef.current = false;
          // Engagement model (2026-07-27): use logout() so any in-flight
          // recording gets a 10s wrap-up tail before stopping, then the
          // stream is released. logout() releases the stream immediately
          // if no recording is active. Falls back to releaseStream() on
          // older bundles that don't expose logout().
          try {
            if (screenRec.logout) screenRec.logout();
            else screenRec.releaseStream();
          } catch (_) { /* noop */ }
        }
      }
      // ---------------------------------------------------------------------

      if (next === 'logout' && onLoggedOut) {
        setTimeout(() => onLoggedOut(), 100);
      }
    } catch (err) {
      // If the backend says this session was superseded by a newer tab, go
      // passive instead of showing a generic error.
      const detail = err?.detail || err?.body?.detail || '';
      if (detail === 'session_superseded' || (err?.status === 409 && String(detail).includes('superseded'))) {
        if (!supersededRef.current) {
          supersededRef.current = true;
          clearWrapTimer();
          setSuperseded(true);
        }
        setError('This softphone is active in another tab or window. Reload this tab to take over.');
      } else {
        setError(detail || err?.message || 'state change failed');
      }
    } finally {
      if (!silent) setBusy(false);
      setShowAux(false);
    }
  };

  // Run the wrap-up countdown.
  //
  // Triggered by two paths, whichever fires first wins (the other becomes a no-op
  // because we already left the 'ready'/'on_call' transient state):
  //   1. Backend SSE event 'cx-queue-call-completed' (Hangup-driven, see ami_subscriber)
  //   2. Client-side 'cx-call-ended-inbound' from useSIP when JsSIP fires `session ended`
  //      on an inbound (queue) call. This is the reliable path.
  //
  // wrap_time_sec source: the SSE event carries it as detail.wrap_time_sec; the
  // client path looks it up from /api/wallboard/personal (which has the agent's
  // first assigned queue with its wrapup_time).
  const startWrapCountdown = async (wrapSecFromEvent) => {
    // Re-entry guard: if a countdown is already running, skip. Prevents
    // double-start when SSE + client paths fire on the same call.
    if (wrapTimerRef.current) {
      console.log('[CX-WRAP] countdown already running, ignoring duplicate trigger');
      return;
    }
    // 2026-08-20 belt-and-suspenders: never enter wrap while a multi-line
    // engagement (held caller / consult / conference) is active. The
    // interaction is not over; wrap here would tear down the held caller.
    if (multiLineActiveRef.current) {
      console.log('[CX-WRAP] startWrapCountdown blocked: multi-line active');
      return;
    }
    let wrapSec = parseInt(wrapSecFromEvent, 10);
    if (!wrapSec || isNaN(wrapSec) || wrapSec < 1) {
      // Lookup from the wallboard personal view (best-effort, never blocking)
      try {
        const personal = await wallboardApi.personal();
        const firstQueue = (personal && personal.queues && personal.queues[0]) || null;
        wrapSec = (firstQueue && firstQueue.wrap_time_sec) || 15;
        console.log('[CX-WRAP] wrap_time_sec resolved from /personal:', wrapSec);
      } catch (err) {
        console.warn('[CX-WRAP] could not look up wrap_time, using default 15s', err);
        wrapSec = 15;
      }
    } else {
      console.log('[CX-WRAP] wrap_time_sec from event:', wrapSec);
    }
    // 2026-08-06 FIX: if the agent was DELIBERATELY not-ready (AUX/Lunch or
    // Not-Ready) when this call started, do NOT run the Ready-bound wrap
    // countdown at all. Restore their chosen state immediately so a call that
    // touched a paused agent can never drag them back to Ready. (Applies to
    // direct-to-extension calls that AUX doesn't gate, and to any race where a
    // queue call landed before the pause took effect.)
    const preCall = preCallStateRef.current;
    if (preCall && (preCall.state === 'aux' || preCall.state === 'notready')) {
      console.log('[CX-WRAP] pre-call state was', preCall.state,
                  '— restoring it instead of Ready (no wrap countdown)');
      preCallStateRef.current = null;
      clearWrapTimer();
      if (preCall.state === 'aux') {
        change('aux', preCall.reason || 'AUX', { silent: true })
          .catch((err) => console.warn('[CX-WRAP] restore-aux failed', err));
      } else {
        change('notready', null, { silent: true })
          .catch((err) => console.warn('[CX-WRAP] restore-notready failed', err));
      }
      return;
    }
    console.log(`[CX-WRAP] starting wrap-up countdown: ${wrapSec}s`);
    setWrapRemain(wrapSec);
    // Set up the interval FIRST (sync), then fire the backend state change.
    // If the API hangs / fails, the countdown still ticks and eventually
    // flips to Ready. This decouples UX from network.
    const started = Date.now();
    wrapTimerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - started) / 1000);
      const remaining = wrapSec - elapsed;
      if (remaining <= 0) {
        clearInterval(wrapTimerRef.current);
        wrapTimerRef.current = null;
        setWrapRemain(0);
        // 2026-08-06 FIX: restore the pre-call state if the agent was
        // deliberately not-ready; otherwise land on Ready as before. (This is
        // the belt to the suspenders above: even if the pre-call short-circuit
        // didn't catch it, we never force Ready over a captured AUX/Not-Ready.)
        const pc = preCallStateRef.current;
        preCallStateRef.current = null;
        if (pc && pc.state === 'aux') {
          console.log('[CX-WRAP] countdown finished, restoring AUX');
          change('aux', pc.reason || 'AUX', { silent: true });
        } else if (pc && pc.state === 'notready') {
          console.log('[CX-WRAP] countdown finished, restoring Not Ready');
          change('notready', null, { silent: true });
        } else {
          console.log('[CX-WRAP] countdown finished, flipping to Ready');
          change('ready', null, { silent: true });
        }
      } else {
        setWrapRemain(remaining);
      }
    }, 250);
    // Fire the backend wrap-state change (async, non-blocking for the timer).
    // silent: don't toggle `busy` (avoids the state buttons flashing dim/bright
    // right after End All / line-X, which looked like they were being clicked).
    change('wrap', null, { silent: true })
      .catch((err) => console.warn('[CX-WRAP] backend wrap state failed', err));
  };

  // On mount: force backend into 'logout' (= removed from queues) so the
  // initial Not Ready badge actually reflects backend state. Without this,
  // a stale subscription from a previous session can leak through and the
  // agent receives queue calls before clicking Ready.
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    (async () => {
      try {
        await softphoneApi.setState('logout', null);
      } catch (err) {
        // Non-fatal — the click handler will retry. Just surface the error.
        console.warn('[CX-STATE] initial logout sync failed', err);
      }
    })();
  }, []);

  // 2026-08-10: load this agent's tenant AUX codes (Admin > AUX Codes).
  // Fail-open: keep the built-in AUX_REASONS default if the fetch fails or
  // returns nothing, so the agent always has reasons to choose from.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await softphoneApi.auxCodes();
        if (cancelled) return;
        if (resp && resp.ok && Array.isArray(resp.codes) && resp.codes.length) {
          setAuxReasons(resp.codes.map((c) => ({ code: c.code, label: c.label })));
        }
      } catch (err) {
        console.warn('[CX-AUX] tenant aux-codes fetch failed; using defaults', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Single-active-tab lock WITHIN this browser (2026-07-29). Belt-and-
  // suspenders to the server-side single-session guard: when THIS tab mounts
  // it announces takeover on a BroadcastChannel; any other softphone tab in
  // the same browser hears it and immediately goes passive (locked controls +
  // banner) without waiting for the server's session-superseded SSE. The
  // newest tab is always the active one (matches the server's last-connect-
  // wins). Reloading a passive tab makes it the newest -> it takes over.
  useEffect(() => {
    let bc = null;
    try {
      bc = new BroadcastChannel('cx-softphone-lock');
    } catch (_) {
      return; // BroadcastChannel unsupported -> rely on server guard only.
    }
    const myId = SOFTPHONE_SESSION_ID;
    const onMsg = (ev) => {
      const d = ev.data || {};
      if (d.type === 'takeover' && d.id && d.id !== myId) {
        // Another tab in this browser just took over -> we go passive.
        if (!supersededRef.current) {
          supersededRef.current = true;
          clearWrapTimer();
          setSuperseded(true);
          setError('This softphone is now active in another tab or window. '
                 + 'Controls are disabled here. Reload this tab to take over.');
        }
      }
    };
    bc.addEventListener('message', onMsg);
    // Announce our takeover to any already-open tabs.
    try { bc.postMessage({ type: 'takeover', id: myId, ts: Date.now() }); } catch (_) {}
    return () => {
      try { bc.removeEventListener('message', onMsg); bc.close(); } catch (_) {}
    };
  }, []);

  // Keep the ref in sync with state on every render.
  stateRef.current = state;

  // 2026-08-17: keep the live-callState ref in sync + drive wrap-up from the
  // TRUE end of the interaction (callState active -> settled idle), NOT from a
  // mid-merge sub-leg `ended` event. A merge transiently dips callState to
  // IDLE before re-entering CONFERENCING within ~1-2s, so we DEBOUNCE 2.5s and
  // only start wrap if callState is STILL idle then.
  // 2026-08-20: mirror the multi-line engagement state (held caller / active
  // consult / 3rd party / conference) so the wrap-trigger can HARD-SUPPRESS
  // wrap while the agent is mid-Add-Call. ROOT CAUSE of the 2026-08-20 test
  // failure: an Add-Call dips SIP callState to idle/ended transiently (the
  // original inbound bridge tears down as the caller goes to hold), the 2.5s
  // wrap-settle fired, wrap-up started MID-CONSULT -> the held caller was then
  // torn down and the agent got dumped to wrap + `cannot_change_state_while_
  // on_call`. AgentStatusBar had NO visibility into mlState. Now it does: while
  // multiLineActive, NO wrap may start (the interaction is NOT over).
  const multiLineActiveRef = useRef(false);
  useEffect(() => {
    multiLineActiveRef.current = !!multiLineActive;
    // If multi-line just became active, cancel any pending wrap-settle so an
    // in-flight Add-Call can never resolve into wrap.
    if (multiLineActive && wrapSettleTimerRef.current) {
      clearTimeout(wrapSettleTimerRef.current);
      wrapSettleTimerRef.current = null;
      console.log('[CX-WRAP] multi-line active -> cancelled pending wrap-settle');
    }
    if (multiLineActive) clearWrapTimer();
  }, [multiLineActive]);

  const prevCallStateRef = useRef(null);
  useEffect(() => {
    const prev = prevCallStateRef.current;
    callStateRef.current = callState;
    const wasActive = !!prev && prev !== 'idle' && prev !== 'ended';
    const nowIdle = !callState || callState === 'idle' || callState === 'ended';
    prevCallStateRef.current = callState;
    // Call became active again -> cancel any pending wrap-settle.
    if (!nowIdle && wrapSettleTimerRef.current) {
      clearTimeout(wrapSettleTimerRef.current);
      wrapSettleTimerRef.current = null;
      console.log('[CX-WRAP] call re-active -> cancelled pending wrap-settle');
    }
    // 2026-08-20: a held caller / active consult means the interaction is NOT
    // over even though SIP callState dipped idle. NEVER arm wrap while
    // multi-line is active.
    if (multiLineActiveRef.current) {
      if (wrapSettleTimerRef.current) {
        clearTimeout(wrapSettleTimerRef.current);
        wrapSettleTimerRef.current = null;
      }
      return;
    }
    if (wasActive && nowIdle && !wrapSettleTimerRef.current) {
      console.log('[CX-WRAP] callState dipped idle -> arming 2.5s wrap-settle');
      wrapSettleTimerRef.current = setTimeout(() => {
        wrapSettleTimerRef.current = null;
        const cs = callStateRef.current;
        const stillIdle = !cs || cs === 'idle' || cs === 'ended';
        if (!stillIdle) {
          console.log('[CX-WRAP] wrap-settle fired but call re-active (', cs, ') -> no wrap');
          return;
        }
        const cur = stateRef.current;
        const transient = ['ready', 'on_call', 'on_call_outbound', 'wrap'].includes(cur);
        if (transient) {
          console.log('[CX-WRAP] interaction ended (settled idle) -> starting wrap');
          startWrapCountdown(null);
        } else {
          console.log('[CX-WRAP] settled idle but state not transient (', cur, ') -> no wrap');
          preCallStateRef.current = null;
        }
      }, 2500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState]);

  // 2026-08-06 FIX: snapshot the pre-call ACD state on the false->true edge of
  // callActive. If the agent was AUX or Not-Ready when a call arrived (a direct
  // extension call, or a race where a queue call landed before the pause
  // applied), remember it so the wrap countdown restores it instead of Ready.
  // Cleared back to null once we're idle again and have consumed it.
  const prevCallActiveRef = useRef(false);
  useEffect(() => {
    const wasActive = prevCallActiveRef.current;
    prevCallActiveRef.current = !!callActive;
    if (callActive && !wasActive) {
      // Call just started — capture the current deliberate not-ready intent.
      const cur = stateRef.current;
      if (cur === 'aux' || cur === 'notready') {
        preCallStateRef.current = { state: cur, reason: cur === 'aux' ? reason : null };
      } else {
        preCallStateRef.current = null; // ready/on_call -> Ready-after-wrap is fine
      }
    }
  }, [callActive, reason]);

  useEffect(() => {
    // States from which a genuine call-end event should launch the 15s
    // wrap-up countdown that lands on Ready.
    //
    // 2026-07-28 FIX (bugs #1 + #3): previously this was ['ready','on_call'],
    // so if the agent's state had flipped to 'wrap' or 'on_call_outbound'
    // DURING a long consult/conference (e.g. a spurious AgentComplete or a
    // backend prior_state restore to 'wrap'), the call-end handler saw a
    // non-transient state and SKIPPED starting the countdown -> the agent got
    // stranded in 'wrap' with no timer. Now we also accept 'wrap' (kick off
    // the countdown that resolves to Ready) and 'on_call_outbound'. We still
    // EXCLUDE 'aux' (deliberate not-ready) and 'logout' so we never drag an
    // intentionally-unavailable agent back to Ready.
    const transientStates = ['ready', 'on_call', 'on_call_outbound', 'wrap'];

    // 2026-08-17: SINGLE debounced wrap starter. ANY end-of-call signal (SSE
    // completed, client session-ended, or callState->idle) routes here. We
    // wait 2.5s and only start wrap if callState is STILL idle then — so a
    // merge's transient IDLE->CONFERENCING dip never triggers wrap.
    const armWrapSettle = (wrapSec) => {
      // 2026-08-20: mid-Add-Call (held caller / consult / conference) is NOT a
      // call end. Refuse to arm wrap while multi-line is active.
      if (multiLineActiveRef.current) {
        console.log('[CX-WRAP] armWrapSettle: multi-line active -> no wrap');
        return;
      }
      const stillOnCall = !!callStateRef.current
        && callStateRef.current !== 'idle' && callStateRef.current !== 'ended';
      if (stillOnCall) {
        console.log('[CX-WRAP] armWrapSettle: still on call/conference -> defer');
        return;
      }
      if (wrapSettleTimerRef.current) return; // already armed
      console.log('[CX-WRAP] armWrapSettle: arming 2.5s settle (wrapSec=', wrapSec, ')');
      wrapSettleTimerRef.current = setTimeout(() => {
        wrapSettleTimerRef.current = null;
        const cs = callStateRef.current;
        const stillIdle = !cs || cs === 'idle' || cs === 'ended';
        if (!stillIdle) {
          console.log('[CX-WRAP] settle fired but call re-active (', cs, ') -> no wrap');
          return;
        }
        const cur = stateRef.current;
        if (!transientStates.includes(cur)) {
          console.log('[CX-WRAP] settle: state not transient (', cur, ') -> no wrap');
          preCallStateRef.current = null;
          return;
        }
        startWrapCountdown(wrapSec);
      }, 2500);
    };

    // Backend SSE path — already wraps detail with wrap_time_sec
    const onSseCompleted = (e) => {
      const detail = e.detail || {};
      console.log('[CX-WRAP] SSE completed received; state=', stateRef.current, 'wrap_time_sec=', detail.wrap_time_sec, 'callState=', callStateRef.current);
      armWrapSettle(detail.wrap_time_sec);
    };
    // Client path — fires from useSIP when a call's session ends
    const onClientCallEnded = () => {
      console.log('[CX-WRAP] client call-ended received; state=', stateRef.current, 'callState=', callStateRef.current);
      armWrapSettle(null);
    };

    // Supervisor force-paused / force-logout (Phase 5b). The backend has
    // already removed/paused the agent in Asterisk via AMI; we just need
    // to flip the UI state so the badge + buttons reflect reality. For
    // force-logout we also call onLoggedOut so the SIP session disconnects.
    const onForcePaused = (e) => {
      const detail = e.detail || {};
      clearWrapTimer();
      setReason(detail.reason || 'forced by supervisor');
      setState('aux');
      setError(`Supervisor: ${detail.reason || 'forced pause'}`);
      setTimeout(() => setError(null), 5000);
    };
    const onForceLogout = (e) => {
      const detail = e.detail || {};
      clearWrapTimer();
      setReason(null);
      setState('notready');
      setError(`Supervisor: ${detail.reason || 'forced logout'}`);
      // The local React state is in sync. The SIP session disconnects too
      // because the parent's onLoggedOut handler calls sip.disconnect().
      if (onLoggedOut) {
        setTimeout(() => onLoggedOut(), 250);
      }
    };

    // 2026-07-27: multi-line Add Call / hold-during-consult must NOT trigger
    // wrap-up. The agent is still on the call. Cancel any pending wrap timer
    // and keep the agent on_call. Fixes the spurious wrap+mute that killed
    // audio on the Hold->Add Call path.
    const onCancelWrap = () => {
      console.log('[CX-WRAP] cancel-wrap received (multi-line active); clearing timer');
      clearWrapTimer();
    };
    // Single-active-session: a newer tab took over -> go passive.
    const onSuperseded = () => {
      if (supersededRef.current) return;
      supersededRef.current = true;
      console.warn('[CX-STATE] this softphone session was superseded by another tab/window');
      clearWrapTimer();
      setSuperseded(true);
      setError('This softphone was opened in another tab or window. '
             + 'Controls are disabled here. Use the most recent tab, or reload this one to take over.');
    };
    window.addEventListener('cx-queue-call-completed', onSseCompleted);
    window.addEventListener('cx-call-ended-inbound', onClientCallEnded);
    window.addEventListener('cx-force-paused', onForcePaused);
    window.addEventListener('cx-force-logout', onForceLogout);
    window.addEventListener('cx-cancel-wrap', onCancelWrap);
    window.addEventListener('cx-session-superseded', onSuperseded);
    return () => {
      window.removeEventListener('cx-queue-call-completed', onSseCompleted);
      window.removeEventListener('cx-call-ended-inbound', onClientCallEnded);
      window.removeEventListener('cx-force-paused', onForcePaused);
      window.removeEventListener('cx-force-logout', onForceLogout);
      window.removeEventListener('cx-cancel-wrap', onCancelWrap);
      window.removeEventListener('cx-session-superseded', onSuperseded);
      clearWrapTimer();
    };
    // Empty deps: the handlers read stateRef.current, so we don't need to
    // re-attach when `state` changes. This avoids tear-down/re-attach
    // races where an event fires between unmount and remount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const btn = (active, color) => ({
    flex: '0 1 auto',
    padding: '4px 8px',
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.2,
    borderRadius: 4,
    border: `1px solid ${active ? color : 'var(--cx-border, #e4e7eb)'}`,
    background: active ? color : 'var(--cx-panel, #fff)',
    color: active ? '#fff' : 'var(--cx-text, #1a202c)',
    cursor: (busy || stateLocked) ? 'not-allowed' : 'pointer',
    opacity: (busy || stateLocked) ? 0.5 : 1,
    whiteSpace: 'nowrap',
    transition: 'all 0.15s ease',
  });
  const lockTitle = 'Finish the current call before changing state.';

  // 2026-08-17: the badge reflects the LIVE call first. While on a call/
  // conference show "On Call" (matching ACD > Queues) regardless of the ACD
  // state, so the agent never sees Ready/Wrap-up while actually on a call.
  const onACall = !!callState && callState !== 'idle' && callState !== 'ended';
  const displayState = onACall ? 'on_call' : state;
  const displayReason = onACall ? null : reason;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        padding: '6px 10px',
        borderBottom: '1px solid var(--cx-border, #e4e7eb)',
        background: 'var(--cx-panel, #fff)',
        display: 'flex',
        gap: 6,
        alignItems: 'center',
        flexWrap: 'wrap',
        rowGap: 6,
      }}>
        {/* Left side: extension + current state pill */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: '0 0 auto' }}>
          <span style={{ fontSize: 10, color: 'var(--cx-muted, #6B7280)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Ext
          </span>
          <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{extension || '—'}</span>
          <StateBadge state={displayState} reason={displayReason} callDirection={callDirection} />
        </div>

        {/* Right side: buttons — wraps to a second row on narrow widths */}
        <div style={{
          display: 'flex',
          gap: 4,
          marginLeft: 'auto',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={() => change(state === 'ready' ? 'notready' : 'ready')}
            disabled={busy || stateLocked}
            style={btn(state === 'ready', '#3BD162')}
            title={stateLocked
              ? lockTitle
              : (state === 'ready'
                ? 'Click to stop taking inbound queue calls'
                : 'Click to start taking inbound queue calls')}
          >
            {state === 'ready' ? '✓ Ready' : '○ Not Ready'}
          </button>
          <button onClick={() => setShowAux((v) => !v)} disabled={busy || stateLocked}
                  style={btn(state === 'aux', '#F5A623')}
                  title={stateLocked ? lockTitle : "Pause from queues with a reason"}>
            ⏸ AUX ▾
          </button>
          <button onClick={() => change('wrap')} disabled={busy || state === 'wrap' || stateLocked}
                  style={btn(state === 'wrap', '#2196F3')}
                  title={stateLocked ? lockTitle : "Post-call wrap-up"}>
            📝 Wrap{wrapRemain > 0 ? ` ${wrapRemain}s` : ''}
          </button>
          <button onClick={() => {
                    if (confirm('Log out of the queue? You will stop receiving calls and the softphone will disconnect.')) {
                      change('logout');
                    }
                  }}
                  disabled={busy || stateLocked}
                  style={btn(false, '#E53935')}
                  title={stateLocked ? lockTitle : "Sign out: remove from all queues + disconnect SIP"}>
            🚪 Logout
          </button>
        </div>

        {error && (
          <div style={{ flexBasis: '100%', fontSize: 10, color: '#E53935', marginTop: 2 }}>
            {error}
          </div>
        )}
      </div>

      {/* AUX reason dropdown */}
      {showAux && (
        <>
          {/* click-outside catcher */}
          <div
            onClick={() => setShowAux(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 49,
            }}
          />
          <div style={{
            position: 'absolute',
            top: 'calc(100% - 4px)',
            right: 10,
            zIndex: 50,
            background: 'var(--cx-panel, #fff)',
            border: '1px solid var(--cx-border, #e4e7eb)',
            borderRadius: 6,
            padding: 4,
            boxShadow: '0 4px 12px -2px rgba(15, 23, 42, 0.15)',
            minWidth: 140,
          }}>
            <div style={{ fontSize: 10, color: 'var(--cx-muted, #6B7280)', padding: '4px 8px',
                          textTransform: 'uppercase', letterSpacing: 0.5 }}>
              AUX reason
            </div>
            {auxReasons.map((r) => (
              <button
                key={r.code}
                onClick={() => change('aux', r.code)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '6px 8px', fontSize: 12,
                  background: 'transparent', border: 0, borderRadius: 4,
                  cursor: 'pointer', color: 'var(--cx-text, #1a202c)',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cx-subtle, #f1f3f5)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {r.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
